import { CircularProgress, createStyles, makeStyles, Theme } from '@material-ui/core';
import { Moment } from 'moment';
import React, { useState } from 'react';

import { TaskType, useCreateFactReviewProcessMutation, useGetPlexDetailsQuery } from '../../../../graphql/gen/types';
import { spacings } from '../../../../theme';
import { notEmpty } from '../../../../utils/notEmpty';
import { PlexName } from '../../common/PlexName';
import { usePlexRoute } from '../../details/usePlexRoute';
import { SplitButtonMenu } from '../../shared/SplitButtonMenu';
import { DialogActions } from '../DialogActions';
import { DialogTopBar } from '../DialogTopBar';
import { useDialogState } from '../useDialogState';
import { ResourceReviewProcessDialogContent } from './ResourceReviewProcessDialogContent';

type Props = {
  plexId: string;
};

type State = {
  selectedFact?: {
    id: string;
    title: string;
    permalink: string;
  };
  authors: { id: string; picture: string; name: string }[];
  reviewers: { id: string; picture: string; name: string }[];
  reviewableBy: Moment | null;
  reviewBy: Moment | null;
};

export function CreateResourceReviewProcessDialog({ plexId }: Props): React.ReactElement {
  const classes = useStyles();
  const { data: plexData } = useGetPlexDetailsQuery({ variables: { plexId } });
  const [state, setState] = useState<State>({ authors: [], reviewers: [], reviewableBy: null, reviewBy: null });
  const [createFactReviewProcess, { loading }] = useCreateFactReviewProcessMutation();
  const [, setDialogState] = useDialogState();
  const [, updatePlexRoute] = usePlexRoute();

  if (!plexData?.loggedIn?.plexById) {
    return <CircularProgress size={24} />;
  }

  const validationError = getValidationError(state);

  const onCreate = () => {
    if (state.selectedFact != null) {
      createFactReviewProcess({
        variables: {
          input: {
            plexId,
            factId: state.selectedFact.id,
            authorIds: state.authors.map((author) => author.id),
            reviewerIds: state.reviewers.map((reviewer) => reviewer.id),
            reviewableBy: state.reviewableBy,
            reviewBy: state.reviewBy,
          },
        },
        update: (cache) => {
          cache.evict({ fieldName: 'tasks' });
        },
      }).then(({ data }) => {
        if (data?.createFactReviewProcess.id != null) {
          setDialogState(null, 'replace');
          const milestoneTask = data.createFactReviewProcess.createdEntities
            .map((entity) =>
              entity.source?.__typename === 'Task' && entity.source.type === TaskType.Milestone
                ? entity.source.permalink
                : null
            )
            .filter(notEmpty)[0];
          if (milestoneTask != null) {
            updatePlexRoute({ selectedItem: { type: 'task', permalink: milestoneTask } });
          } else {
            throw new Error(
              `Could not find milestone in ${JSON.stringify(data.createFactReviewProcess.createdEntities)}`
            );
          }
        }
      });
    }
  };

  return (
    <div className={classes.root}>
      <DialogTopBar>
        <PlexName
          name={plexData.loggedIn.plexById.name}
          color={plexData.loggedIn.plexById.color}
          className={classes.plexName}
        />
      </DialogTopBar>

      <ResourceReviewProcessDialogContent data={state} onChange={setState} mode="create" />

      <DialogActions
        cta={
          <SplitButtonMenu
            disabled={validationError != null || loading}
            {...(validationError != null ? { tooltipProps: { title: validationError } } : {})}
            color="primary"
            variant="contained"
            options={[{ onClick: onCreate, label: 'Create' }]}
          />
        }
      />
    </div>
  );
}

const getValidationError = (state: State): string | null => {
  if (state.selectedFact == null) {
    return 'You must select a Resource.';
  } else if (state.reviewBy == null) {
    return 'You must select a Review By date.';
  } else {
    return null;
  }
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
    },
    plexName: {
      marginLeft: spacings.xlarge,
      marginTop: spacings.normal,
      marginBottom: spacings.normal,
    },
  })
);
